import localforage from "localforage";
import axios from "axios";
import { Vector as VectorSource, ImageWMS } from "ol/source";
import { dataAPI } from "../../api/axios";
import { all } from "ol/loadingstrategy";
import SLDParser from "geostyler-sld-parser";
import OpenLayersParser from "geostyler-openlayers-parser";
import { Image as ImageLayer } from "ol/layer";
import GeoJSON from "ol/format/GeoJSON";
import produce from "immer";
import Stroke from "ol/style/Stroke.js";
import Style from "ol/style/Style.js";
import VectorLayer from "ol/layer/Vector.js";
import { addArcGISImage } from "./mapUtils";

const sldParser = new SLDParser();
const olParser = new OpenLayersParser();

export class HandleAddLayers {
    /**
     * Class responsible for adding different types of layers to a map.
     * @param {Map} map - The map instance to which layers will be added.
     * @param {Function} loadingStartCallback - The callback function to handle loading start.
     * @param {Function} loadingFinishCallback - The callback function to handle loading finish.
     */
    constructor(map, loadingStartCallback, loadingFinishCallback) {
        this.map = map;
        this.start = loadingStartCallback;
        this.finish = loadingFinishCallback;
    }

    /**
     * Adds a layer to the map based on the provided properties.
     * @param {Object} property - The properties of the layer.
     * @param {boolean} Visible - Determines if the layer is initially visible.
     * @param {number} defaultZindex - The z-index of the layer.
     */
    addLayer(property, Visible, defaultZindex) {
        switch (property.Type) {
            case "GeoJson":
                addVector(
                    this.map,
                    property,
                    Visible,
                    defaultZindex,
                    this.start,
                    this.finish
                );
                break;
            case "WMS":
                addWMSImageLayer(this.map, Visible, property.Opacity, property);
                break;
            case "ArcGISImageService":
                addArcGISImage(this.map, property);
                break;
            case "ArcGISMapService":
                addArcGISImage(this.map, property);
                break;
            default:
                console.log(
                    `Layer with type: ${property.Type} unaccounted for; layer was not added to map`
                );
        }
    }
}

/**
 * @typedef {object} WMSLayerProperties
 * @property {string} URL
 * @property {string} Layer
 * @property {string} Style
 * @property {string} Format
 * @property {string} Id
 * @property {string} ZIndex
 */

/**
 * Adds a WMS image layer to a map using the OpenLayers library.
 *
 * @param {object} map - The map object to which the layer will be added.
 * @param {boolean} Visible - Whether the layer should be initially visible.
 * @param {number} Opacity - The opacity of the layer (0 to 1).
 * @param {WMSLayerProperties} property - An object containing the properties of the WMS layer, including the URL, Geoserver Layer and Style names, Format, Id, and ZIndex.
 * @returns {void}
 */
export const addWMSImageLayer = (map, Visible, Opacity, property) => {
    const l = new ImageLayer({
        source: new ImageWMS({
            url: property.URL,
            params: {
                LAYERS: property.Layer,
                STYLES: property.Style,
                FORMAT: property.Format ?? "image/vnd.jpeg-png8",
                TRANSPARENT: true,
            },
            crossOrigin: "Anonymous",
            ratio: 1.5,
            serverType: "geoserver",
        }),
        properties: {
            Id: property.Id,
        },
    });
    l.setZIndex(property.ZIndex ?? 100);
    l.setVisible(Visible);
    l.setOpacity(Opacity ?? 1);
    map.addLayer(l);
};

// property is an array of objects, example: {Id: '5xBlAM', Visible: false, Style: anytown_Parcels}
export const addVector = async (
    map,
    property,
    Visible,
    defaultZindex,
    loadingStartCallback,
    loadingFinishCallback
) => {
    const geoJson = await getVectorAndStyle(
        property,
        map,
        loadingStartCallback,
        loadingFinishCallback
    );
    const ZIndex = property.ZIndex ?? defaultZindex;
    geoJson.setProperties({ Id: `${property.Id}` });
    geoJson.setVisible(Visible);
    geoJson.setZIndex(ZIndex);
    geoJson.setOpacity(property.Opacity ?? 1);
    map.addLayer(geoJson);

    const wmsProps = { ...property.Label, ZIndex };
    if (property.hasOwnProperty("Label")) {
        addWMSImageLayer(map, Visible, property.Opacity, wmsProps);
    }
};

const getVectorAndStyle = async (
    property,
    map,
    loadingStartCallback,
    loadingFinishCallback
) => {
    try {
        const geoJson = new VectorLayer({
            source: await getVectorData(
                property,
                loadingStartCallback,
                loadingFinishCallback
            ),
        });
        try {
            const style = await getStyle(property.Style, map);
            geoJson.setStyle(style);
            return geoJson;
        } catch {
            console.error("could not get style", property.Style);
        }
    } catch {
        console.error("could not get layer ", property.DisplayName);
    }
};

const getVectorData = async (property, loadingStartCallback, loadingFinishCallback) => {
    const vectorSource = new VectorSource({
        format: new GeoJSON(),
        strategy: all,
        loader: function (extent, resolution, projection, success, failure) {
            const proj = projection.getCode();
            let url = `Layers/${property.Id}/Data`;
            var params = {
                bbox: extent.join(","),
            };
            var totalFeatures;

            var loadFeatures = async function () {
                loadingStartCallback();
                dataAPI
                    .get(url, {
                        params: params,
                    })
                    .then(async function (response) {
                        let data = "";
                        // if response is 304 check cache
                        if (response.status === 304) {
                            let urlParts = response.config.url.split("/");
                            try {
                                //pull data from IndexedDB
                                data = await localforage.getItem(
                                    `${response.config.params?.key}_${urlParts[1]}_${response.config.params?.PageNumber}_${response.config.params?.PageSize}`
                                );
                            } catch (error) {
                                console.log(error);
                            }
                        } else {
                            //Else process the data as normal
                            data = response.data;
                        }

                        totalFeatures = data.TotalRecords;
                        var features = vectorSource.getFormat().readFeatures(data.Data);
                        vectorSource.addFeatures(features);
                        // Check if there are more pages to load

                        if (
                            data.PageNumber * data.PageSize < totalFeatures &&
                            data.PageNumber === 1
                        ) {
                            let pagesToLoad = Math.ceil(totalFeatures / data.PageSize);
                            if (pagesToLoad > 1) {
                                for (let index = 2; index <= pagesToLoad; index++) {
                                    params.PageNumber = index;
                                    params.PageSize = data.PageSize;
                                    loadFeatures();
                                }
                            }
                        }
                        loadingFinishCallback();
                        success(features);
                    })
                    .catch(function (error) {
                        console.error(error);
                        console.log(property.DisplayName, "layer failed to load");
                        loadingFinishCallback();
                        failure();
                    });
            };
            params.PageSize = 5000;
            params.PageNumber = 1;
            loadFeatures();
        },
    });

    return vectorSource;
};

export const getStyle = async (keyName, map) => {
    try {
        const res = await axios.get(
            `${process.env.REACT_APP_AZURE_CDN_URL}/media/sld/${keyName}.sld`,
            {
                headers: {},
            }
        );
        const sldObject = await sldParser
            .readStyle(res.data)
            .then(({ output: sldObject }) => {
                return sldObject;
            });
        // Will need to write custom style function
        // resolution = map.getView().getResolution()
        // units = map.getView().getProjection().getUnit()
        // dpi = 25.4mm per inch / .28 pixels per mm
        // Scale = resolution * inchesPerUnit * dpi
        // See:
        //   https://github.com/terrestris/ol-util/blob/master/src/MapUtil/MapUtil.ts
        //   https://github.com/geostyler/geostyler-openlayers-parser/blob/master/src/OlStyleParser.ts
        // const metersPerUnit_ = 0.3048006096012192;

        const metersPerUnit_ = map.getView().getProjection().metersPerUnit_;
        const sldHack = produce(sldObject, (draft) => {
            draft.rules.map((rule) => {
                if (rule.scaleDenominator !== undefined) {
                    if (rule.scaleDenominator.max !== undefined) {
                        rule.scaleDenominator.max = +(
                            rule.scaleDenominator.max / metersPerUnit_
                        ).toFixed(0);
                    }
                    if (rule.scaleDenominator.min !== undefined) {
                        rule.scaleDenominator.min = +(
                            rule.scaleDenominator.min / metersPerUnit_
                        ).toFixed(0);
                    }
                }
            });
        });

        const style = await olParser.writeStyle(sldHack).then(({ output: olStyle }) => {
            return olStyle;
        });
        return style;
    } catch (err) {
        console.error(err);
        return defaultStyle;
    }
};

const defaultStyle = new Style({
    stroke: new Stroke({
        color: "black",
        width: 2,
    }),
});
